<script>
  import { afterUpdate, createEventDispatcher, onMount, onDestroy } from "svelte"
  const dispatch = createEventDispatcher()
  import singlePublish, { removeDataSinglePublish } from "../../../Publish/SinglePublish/Stores/singlePublishStore"
  import LoadingScreen from "../../../Publish/SinglePublish/Components/loadingScreen.svelte"
  import notify from "../../../../Script/notify"
  import { loadTooltip } from "../../../../Script/loadTooltip"
  import ApplicationState from "../../../../Stores/applicationStates"
  import { getRequest, deleteRequest } from "../../../../Script/apiMethods"

  export let json

  let showReleaseConfirmModal = false

  /**
   * @type {Boolean} - whether to disable next button or not
   */
  let showLoader = false

  let loaderMsg = "Fetching Document list"

  let docketStatus = ""

  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  let closeNotification = {
    close() {},
  }

  let resumeURL = ""
  let tooltipContent = ""

  $: {
    if (json.docket) {
      resumeURL = `#/signing/single/docket?id=${json.docket}`
      tooltipContent = "Resume Docket"
    } else {
      resumeURL = json.bulk ? `#/publish/bulk/generate-file?id=${json.bulk}` : `#/publish/single?documentId=${json.id}`
      tooltipContent = !json.fileHash ? "Waiting for file" : !json.dataHash ? "Waiting for metadata" : "Waiting for Publish"
    }
  }

  onMount(() => {
    window.addEventListener("resize", loadTooltip)
  })

  onDestroy(() => {
    window.removeEventListener("resize", loadTooltip)
  })

  afterUpdate(() => {
    loadTooltip()
  })

  const handleRelease = async (e) => {
    try {
      showLoader = true

      if (json.docket) {
        loaderMsg = "Releasing Docket"

        const force = !(docketStatus == "REJECTED")

        // Releasing Docket
        await deleteRequest(`/api/pdf-signing/v2/docket/${json.docket}?force=${force}`, {
          headers: {
            "msign-org-token": $ApplicationState.msignToken,
          },
        })
      }

      loaderMsg = "Releasing Document"

      // Releasing Document
      await deleteRequest(`/api/swagger/v2/documents/${json.id}/release?force=${json.docket ? true : false}`, {
        headers: {
          "x-access-token": $ApplicationState.token,
        },
      })

      await removeDataSinglePublish(json.id)
      if (json.id == $singlePublish.documentId) $singlePublish = {}

      notify.success("Docket released successfully")

      loaderMsg = "Fetching Document list"

      dispatch("fetchPendingList", json.id)
    } catch (error) {
      closeNotification.close()
      const info = "PAGE: Pending Documents\nEVENT: While releasing pending single document."
      closeNotification = notify.danger(error, info)
    } finally {
      showReleaseConfirmModal = false
      showLoader = false
    }
  }

  const checkDocketStatus = async () => {
    try {
      if (!json.docket) {
        showReleaseConfirmModal = true
        return
      }

      const { docket } = await getRequest(`/api/pdf-signing/v2/docket/${json.docket}`, {
        headers: {
          "msign-org-token": $ApplicationState.msignToken,
        },
      })

      docketStatus = docket.status

      showReleaseConfirmModal = true
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Pending Documents\nEVENT: While checking docket status before releasing pending single document."
      closeNotification = notify.danger(error, info)
    }
  }
</script>

<div class="mx-auto mt-3 flex h-full w-full flex-col border-l-8 border-transparent hover:shadow-md dark:border-y-2 dark:border-r-2 dark:border-y-transparent dark:border-r-transparent {json.notRevoked && json.dataHash && json.fileHash && json.datePublished ? ' border-green-500' : json.datePublished ? 'border-red-500 dark:hover:border-red-500' : 'border-yellow-500 dark:hover:border-yellow-500'} gap-3 rounded-r-md bg-white px-6 py-3 dark:bg-lightgray">
  <div class="flex w-full items-center justify-between gap-3">
    <div class="flex w-full items-center gap-4 truncate">
      <slot />
      <div class="flex w-full flex-col space-y-1.5 truncate">
        <!-- <p class="truncate text-xs font-semibold text-gray-800 sm:text-base">{json.id}</p> -->
        <a href={json.bulk ? `#/publish/bulk/generate-file?id=${json.bulk}` : json.docket ? `#/signing/single/docket?id=${json.docket}` : `#/publish/single?documentId=${json.id}`} class="w-full select-none truncate text-xs font-semibold text-gray-800 dark:text-gray-200 sm:text-base">
          <div class="w-full truncate hover:text-blue-500">
            {json.options.title ? json.options.title : "Untitled document"}
            {json.recName ? `for ${json.recName}` : ""}
          </div>
        </a>
        <!-- <p class="select-none truncate text-xs font-semibold text-gray-800 sm:text-base">Un titled document for {json.recName}</p> -->
        <div class="flex items-center gap-3">
          <p title={json.datePublished ? "Published On" : "PENDING"} class="truncate text-xs font-normal text-gray-200 sm:text-base">
            <i class="fa-solid fa-calendar-days" />
            &nbsp;&nbsp;{new Date(json.dateCreated).toString().slice(0, 24)}
          </p>
        </div>
      </div>
    </div>
    <div class="flex select-none items-center justify-end xl:justify-between">
      <a href={resumeURL} data-tooltip={tooltipContent} tooltip-position="top" class="flex h-9 cursor-pointer items-center gap-2 rounded-full px-3 text-sm font-medium text-blue-500 hover:bg-blue-500 hover:text-white active:text-white sm:px-5">
        <i class="fa-solid fa-circle-play" />
        <span class="hidden lg:block">Resume</span>
      </a>

      <button name="tooltip" id="Release" tooltip-position="top" on:click={checkDocketStatus} class="flex h-9 cursor-pointer items-center justify-center gap-2 rounded-full px-3 text-sm font-medium text-red-500 hover:bg-red-500 hover:text-white active:text-white disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent disabled:active:bg-transparent sm:px-5">
        <i class="fa-solid fa-trash-can" />
        <span class="hidden lg:block">Release</span>
      </button>
    </div>
  </div>
</div>
{#if showReleaseConfirmModal}
  <div class="fixed inset-0 -top-3 z-50 mt-0 flex flex-col items-center justify-center gap-3 overflow-hidden bg-[#000000cc] backdrop-blur-md">
    <div class="relative m-6 transform select-none rounded-md bg-white text-left transition-all dark:bg-bodyblack md:m-0 md:w-[700px]">
      <div class="flex w-full items-center justify-center rounded-md bg-white p-3 text-black dark:bg-bodyblack md:p-6">
        <h1 class="select-none text-xl font-bold text-black dark:text-white lg:text-2xl xl:text-2xl">Release Document</h1>
      </div>

      <div class="break-words p-3 text-center text-gray-400 md:p-6">
        {#if json.docket}
          {#if docketStatus == "REJECTED"}
            Please confirm that the document and docket are ready to be released. Once released, the docket won't be viewable anymore.
          {:else}
            Please confirm the release of the document, as it is linked to the docket. Once the document is released, the docket will also be released. Once released, the docket won't be viewable anymore.
          {/if}
        {:else}
          Do you want to&nbsp; <strong>Release</strong>
          &nbsp;this
          <strong>&nbsp;Document.&nbsp;</strong>
        {/if}
      </div>

      <div class="flex flex-row items-center justify-center gap-3 rounded-md bg-white p-2 dark:bg-bodyblack sm:flex md:gap-0 md:p-4">
        <button
          on:click={() => {
            showReleaseConfirmModal = false
          }}
          type="button"
          class="flex h-9 min-h-full min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-[#f7f7f7] px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-blue-500 hover:text-white focus:outline-none disabled:cursor-not-allowed dark:bg-lightgray dark:text-white dark:hover:bg-blue-500 sm:ml-3 sm:w-auto"
        >
          <span>
            <i class="fa-solid fa-circle-xmark" />
          </span>
          &nbsp;&nbsp;
          <span class="select-none">&nbsp;Cancel</span>
        </button>
        <button on:click={handleRelease} type="button" class="flex h-9 min-h-full min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] bg-[#f7f7f7] px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-red-500 hover:text-white focus:outline-none disabled:cursor-not-allowed dark:bg-lightgray dark:text-white dark:hover:bg-red-500 sm:ml-3 sm:w-auto">
          <span>
            <i class="fa-solid fa-circle-check" />
          </span>
          &nbsp;&nbsp;
          <span class="select-none">&nbsp;Confirm</span>
        </button>
      </div>
    </div>
  </div>
{/if}

{#if showLoader}
  <LoadingScreen msg={loaderMsg} />
{/if}

<style>
  ::-webkit-scrollbar-thumb {
    background: #f7f7f7;
    border-radius: 20px;
  }
  ::-webkit-scrollbar {
    height: 4px;
  }
</style>
